/**
 * Matplotlib Code Snippets
 * Reusable code generation building blocks
 *
 * Every function accepts an optional `s` (style) object produced by
 * resolveStyle(theme). When omitted, brutalist defaults are used so
 * existing call-sites keep working.
 */

import { deploymentProfile } from '../../config/deploymentProfile';

const DEFAULTS = {
  foreground: '#000000',
  titleColor: '#000000',
  titleSize: 14,
  labelColor: '#000000',
  labelSize: 12,
  tickColor: '#000000',
  tickSize: 11,
  axisColor: '#000000',
  gridColor: '#000000',
  gridAlpha: 0.1,
  spineWidth: 1,
  figBackground: '#FFFFFF',
  plotBackground: '#FFFFFF',
  legendTextColor: '#000000',
  legendSize: 10,
  valueLabelSize: 10,
  referenceLineColor: '#C44B3D',
  fontLabel: 'Cohere Text',
  fontNumber: 'Cohere Text',
};

function v(s, key) {
  return (s && s[key] != null) ? s[key] : DEFAULTS[key];
}

/**
 * Standard imports and font setup
 */
export function matplotlibSetup(s) {
  const {
    pythonFamily,
    pythonFileName,
    pythonLabelFamily,
    pythonLabelFileName,
    pythonNumberFamily,
    pythonNumberFileName,
  } = deploymentProfile.font;
  const labelFamily = v(s, 'fontLabel') || pythonLabelFamily || pythonFamily;
  const numberFamily = v(s, 'fontNumber') || pythonNumberFamily || pythonFamily;
  const labelFile = pythonLabelFileName || pythonFileName;
  const numberFile = pythonNumberFileName || pythonFileName;
  return `import matplotlib.pyplot as plt
import numpy as np
import pandas as pd
import matplotlib.font_manager as fm
import matplotlib.colors as mcolors

# Prefer bundled deployment fonts when available, otherwise fall back to installed families.
def _coplot_font_properties(font_path, family):
    try:
        return fm.FontProperties(fname=font_path)
    except Exception:
        return fm.FontProperties(family=[family, 'DejaVu Sans', 'Arial', 'Helvetica', 'sans-serif'])

_coplot_label_font = _coplot_font_properties('fonts/${labelFile}', '${labelFamily}')
_coplot_number_font = _coplot_font_properties('fonts/${numberFile}', '${numberFamily}')
plt.rcParams['font.family'] = _coplot_label_font.get_name()

# Set styling for all text and lines
plt.rcParams['text.color'] = '${v(s, 'foreground')}'
plt.rcParams['axes.labelcolor'] = '${v(s, 'labelColor')}'
plt.rcParams['xtick.color'] = '${v(s, 'tickColor')}'
plt.rcParams['ytick.color'] = '${v(s, 'tickColor')}'
plt.rcParams['axes.edgecolor'] = '${v(s, 'axisColor')}'

# Grid styling
plt.rcParams['grid.color'] = '${v(s, 'gridColor')}'
plt.rcParams['grid.alpha'] = ${v(s, 'gridAlpha')}

def _coplot_luminance_from_rgba(rgba):
    r, g, b = mcolors.to_rgb(rgba)
    def _linear(c):
        return c / 12.92 if c <= 0.03928 else ((c + 0.055) / 1.055) ** 2.4
    return 0.2126 * _linear(r) + 0.7152 * _linear(g) + 0.0722 * _linear(b)

def _coplot_text_color_for_rgba(rgba, preferred='${v(s, 'foreground')}'):
    try:
        preferred_l = _coplot_luminance_from_rgba(mcolors.to_rgba(preferred))
        bg_l = _coplot_luminance_from_rgba(rgba)
        lighter = max(preferred_l, bg_l)
        darker = min(preferred_l, bg_l)
        ratio = (lighter + 0.05) / (darker + 0.05)
        if ratio >= 4.5:
            return preferred
    except Exception:
        pass
    bg_l = _coplot_luminance_from_rgba(rgba)
    return '#111111' if bg_l > 0.5 else '#FFFFFF'`;
}

/**
 * Figure creation with theme-aware background
 */
export function createFigure(width = 5.5, height = 4.5, dpi = 300, s) {
  const figBg = v(s, 'figBackground');
  const plotBg = v(s, 'plotBackground');
  return `
# Create figure and axis with publication-quality settings
fig, ax = plt.subplots(figsize=(${width}, ${height}), dpi=${dpi})
fig.patch.set_facecolor('${figBg}')
ax.set_facecolor('${plotBg}')`;
}

/**
 * Title code generation
 */
export function titleCode(config, s) {
  if (!config.title) return '';
  return `
# Add title (outside plot area, top left)
fig.suptitle('${config.title}', fontsize=${v(s, 'titleSize')}, x=0.125, y=0.98, ha='left',
             color='${v(s, 'titleColor')}', fontproperties=_coplot_label_font)`;
}

/**
 * Grid code generation
 */
export function gridCode(config, axis = 'y', s) {
  if (!config.showGrid) return "# Grid disabled";
  return `ax.grid(axis='${axis}', alpha=${v(s, 'gridAlpha')}, color='${v(s, 'gridColor')}', linestyle='--', linewidth=0.5, zorder=0)
ax.set_axisbelow(True)`;
}

/**
 * Axis labels code generation
 */
export function axisLabelsCode(config, s) {
  return `
# Customize axis labels
ax.set_xlabel('${config.xlabel || ''}', fontsize=${v(s, 'labelSize')}, labelpad=10, color='${v(s, 'labelColor')}', fontproperties=_coplot_label_font)
ax.set_ylabel('${config.ylabel || ''}', fontsize=${v(s, 'labelSize')}, color='${v(s, 'labelColor')}', labelpad=10, fontproperties=_coplot_label_font)`;
}

/**
 * Y-axis range code generation
 */
export function yAxisRangeCode(config) {
  const hasYMin = config.yMin && config.yMin.trim() !== '';
  const hasYMax = config.yMax && config.yMax.trim() !== '';

  if (!hasYMin && !hasYMax) return '# Auto y-axis range';

  const parts = [];
  if (hasYMin) parts.push(`bottom=${config.yMin}`);
  if (hasYMax) parts.push(`top=${config.yMax}`);

  return `ax.set_ylim(${parts.join(', ')})`;
}

/**
 * X-axis range code generation (for horizontal charts)
 */
export function xAxisRangeCode(config) {
  const hasXMin = config.xMin && config.xMin.trim() !== '';
  const hasXMax = config.xMax && config.xMax.trim() !== '';

  if (!hasXMin && !hasXMax) return '# Auto x-axis range';

  const parts = [];
  if (hasXMin) parts.push(`left=${config.xMin}`);
  if (hasXMax) parts.push(`right=${config.xMax}`);

  return `ax.set_xlim(${parts.join(', ')})`;
}

/**
 * Reference line code generation
 */
export function referenceLineCode(config, axis = 'horizontal', s) {
  const hasReferenceLine = config.showReferenceLine &&
    config.referenceValue &&
    config.referenceValue.trim() !== '';

  if (!hasReferenceLine) return '';

  const legendSz = v(s, 'legendSize');
  if (axis === 'horizontal') {
    return `
# Add reference line
ax.axhline(y=${config.referenceValue}, color='${v(s, 'referenceLineColor')}', linestyle='--',
          linewidth=2, alpha=0.7, label='${config.referenceLabel || 'Reference'}')
ax.legend(loc='best', fontsize=${legendSz})`;
  } else {
    return `
# Add reference line
ax.axvline(x=${config.referenceValue}, color='${v(s, 'referenceLineColor')}', linestyle='--',
           linewidth=2, alpha=0.7, label='${config.referenceLabel || 'Reference'}')
ax.legend(loc='best', fontsize=${legendSz})`;
  }
}

/**
 * Label rotation code
 */
export function labelRotationCode(config, axis = 'x') {
  if (!config.labelRotation || config.labelRotation === 0) return '';

  const ha = config.labelRotation !== 0 ? 'right' : 'center';
  return `
# Rotate ${axis}-axis labels
plt.${axis}ticks(rotation=${config.labelRotation}, ha='${ha}')`;
}

/**
 * Spine styling (remove top/right, style bottom/left)
 */
export function spineCode(s, xTickRole = 'number', yTickRole = 'number') {
  const sw = v(s, 'spineWidth');
  const ac = v(s, 'axisColor');
  const xFont = xTickRole === 'label' ? '_coplot_label_font' : '_coplot_number_font';
  const yFont = yTickRole === 'label' ? '_coplot_label_font' : '_coplot_number_font';
  return `
# Remove top and right spines for cleaner look
ax.spines['top'].set_visible(False)
ax.spines['right'].set_visible(False)
ax.spines['bottom'].set_linewidth(${sw})
ax.spines['left'].set_linewidth(${sw})
ax.spines['bottom'].set_color('${ac}')
ax.spines['left'].set_color('${ac}')

# Adjust spine positions
ax.spines['left'].set_position(('outward', 2))
ax.spines['bottom'].set_position(('outward', 2))

# Apply role-based tick fonts
for tick in ax.get_xticklabels():
    tick.set_fontproperties(${xFont})
    tick.set_fontsize(${v(s, 'tickSize')})
for tick in ax.get_yticklabels():
    tick.set_fontproperties(${yFont})
    tick.set_fontsize(${v(s, 'tickSize')})`;
}

/**
 * Figure finish (tight layout + show)
 * @param {string|null} saveName - optional filename for the save comment
 * @param {number[]} rect - tight_layout rect [left, bottom, right, top], defaults to [0, 0.03, 1, 0.97]
 */
export function finishCode(saveName = null, rect = [0, 0.03, 1, 0.97]) {
  let code = `
# Tight layout
plt.tight_layout(rect=[${rect.join(', ')}])`;

  if (saveName) {
    code += `

# Save figure (uncomment to save)
# plt.savefig('${saveName}.png', dpi=300, bbox_inches='tight')`;
  }

  code += `

# Display the plot
plt.show()`;

  return code;
}

/**
 * Bar ordering/sorting code
 */
export function sortingCode(config, dataVar = 'values', labelsVar = 'labels') {
  if (config.barOrdering === 'original' || !config.barOrdering) return '';

  const direction = config.barOrdering === 'descending' ? '[::-1]' : '';

  return `
# Sort data
sorted_indices = np.argsort(${dataVar})${direction}
${labelsVar} = [${labelsVar}[i] for i in sorted_indices]
${dataVar} = [${dataVar}[i] for i in sorted_indices]`;
}

/**
 * Value labels on bars code
 */
export function valueLabelsCode(config, orientation = 'vertical', s) {
  if (!config.showValues) return '# Value labels disabled';

  const decimals = config.valueDecimals ?? 1;
  const fg = v(s, 'foreground');
  const valueLabelSize = v(s, 'valueLabelSize');

  if (orientation === 'vertical') {
    return `# Add value labels on bars
for bar in bars:
    height = bar.get_height()
    txt_color = _coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${fg}')
    ax.text(bar.get_x() + bar.get_width()/2., height,
            f'{height:.${decimals}f}',
            ha='center', va='bottom', fontsize=${valueLabelSize}, color=txt_color,
            fontproperties=_coplot_number_font)`;
  } else {
    const plotBg = v(s, 'plotBackground');
    return `# Add value labels on bars
for bar in bars:
    width = bar.get_width()
    txt_color = _coplot_text_color_for_rgba(mcolors.to_rgba('${plotBg}'), preferred='${fg}')
    ax.text(width, bar.get_y() + bar.get_height()/2.,
            f'{width:.${decimals}f}',
            ha='left', va='center', fontsize=${valueLabelSize}, color=txt_color,
            fontproperties=_coplot_number_font,
            bbox=dict(boxstyle='round,pad=0.3', facecolor='${plotBg}', edgecolor='none', alpha=0.7))`;
  }
}
